import dayjs from 'dayjs';
import { safeParseDateInput } from '../../../../helpers/date-time-helpers';
import { buildFlowByPhaseQuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links';
import type { QuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links.type';
import { PHASE_COLORS } from '../artifact-colors';

const METRIC_KEY_PATTERN = /^(tasks|points)_/;

type FlowByPhaseMetric = 'tasks' | 'points';
type FlowByPhaseChartDataPoint = Record<string, string | number | null>;

interface FlowByPhaseBoardConfig {
  id: string;
  name: string;
  work_period_ids?: string[];
}

interface FlowByPhaseWorkPeriod {
  id: string;
}

const PHASE_LABELS: Record<string, string> = {
  backlog: 'Backlog',
  blocked: 'Blocked',
  wip: 'WIP',
  review: 'Review',
  test: 'Test',
  deployable: 'Deployable',
  done: 'Done',
};

/** Ordered phase IDs derived from PHASE_COLORS keys to stay in sync. */
const PHASE_ORDER = Object.keys(PHASE_COLORS);

/**
 * Checks whether an artifact data object has the expected Flow-by-Phase wrapper shape.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload exposes a work_periods array.
 */
function isWorkPeriodsPayload(data: Record<string, unknown>): boolean {
  return Array.isArray(data.work_periods);
}

/**
 * Reads the initial work-period index from the export-route `initialState` seed.
 * Defaults to 0 when no seed is provided or the value is invalid.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {number} A non-negative integer index.
 */
function getInitialWorkPeriodIndex(initialState: Record<string, unknown> | undefined): number {
  const value = initialState?.workPeriodIndex;
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  return 0;
}

/**
 * Reads the initial metric (tasks vs points) from the export-route `initialState` seed.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed.
 * @returns {FlowByPhaseMetric} `'points'` when the seed is `'points'`; `'tasks'` otherwise.
 */
function getInitialMetric(initialState: Record<string, unknown> | undefined): FlowByPhaseMetric {
  return initialState?.metric === 'points' ? 'points' : 'tasks';
}

/**
 * Builds quick links scoped to the currently selected Flow-by-Phase work period.
 *
 * @param {Record<string, unknown>} data - Raw artifact data used by the quick-link builder.
 * @param {FlowByPhaseWorkPeriod} workPeriod - Currently selected work period.
 * @param {Record<string, unknown> | undefined} config - Raw artifact config containing board context.
 * @returns {QuickLink[]} Quick links scoped to the selected work period.
 */
function buildScopedFlowByPhaseQuickLinks(
  data: Record<string, unknown>,
  workPeriod: FlowByPhaseWorkPeriod,
  config?: Record<string, unknown>,
): QuickLink[] {
  const scopedConfig = config ? { ...config } : {};
  const rawBoards = scopedConfig.boards;
  const boards = Array.isArray(rawBoards) ? (rawBoards as FlowByPhaseBoardConfig[]) : [];
  const scopedBoards = boards.map((board) => ({
    ...board,
    work_period_ids: board.work_period_ids?.includes(workPeriod.id) ? [workPeriod.id] : board.work_period_ids,
  }));

  return buildFlowByPhaseQuickLink(data, { ...scopedConfig, boards: scopedBoards });
}

/**
 * Discovers phase IDs present in the chart data, preserving the known display order.
 *
 * @param {FlowByPhaseChartDataPoint[]} chartData - Chart data rows for one work period.
 * @returns {string[]} Ordered phase IDs present in the chart data.
 */
function getChartPhases(chartData: FlowByPhaseChartDataPoint[]): string[] {
  if (!chartData.length) return [];
  const firstRow = chartData[0];
  const seen = new Set<string>();
  for (const key of Object.keys(firstRow)) {
    if (key === 'date') continue;
    const phase = key.replace(METRIC_KEY_PATTERN, '');
    seen.add(phase);
  }
  return PHASE_ORDER.filter((phase) => seen.has(phase));
}

/**
 * Nullifies phase metric values for chart data points dated after today.
 * Fails closed: if a date cannot be parsed, metric values are also nullified.
 * Only keys matching `tasks_*` / `points_*` are affected — all other fields are preserved.
 *
 * @param {Array<Record<string, string | number | null>>} chartData - Array of chart data points, each a record with string keys and string, number, or null values.
 * @returns {Array<Record<string, string | number | null>>} New array of chart data points, with future metric values nullified.
 */
function nullifyFutureChartData(chartData: FlowByPhaseChartDataPoint[]): FlowByPhaseChartDataPoint[] {
  if (!chartData?.length) return [];

  const today = dayjs().endOf('day');

  return chartData.map((dataPoint) => {
    const dataPointDate = safeParseDateInput(dataPoint.date as string);
    const shouldNullify = !dataPointDate || dataPointDate.valueOf() > today.valueOf();

    if (shouldNullify) {
      return Object.fromEntries(
        Object.entries(dataPoint).map(([key, value]) => (METRIC_KEY_PATTERN.test(key) ? [key, null] : [key, value])),
      ) as typeof dataPoint;
    }

    return dataPoint;
  });
}

export {
  PHASE_LABELS,
  PHASE_ORDER,
  buildScopedFlowByPhaseQuickLinks,
  getChartPhases,
  getInitialMetric,
  getInitialWorkPeriodIndex,
  isWorkPeriodsPayload,
  nullifyFutureChartData,
};
export type { FlowByPhaseMetric };
